import { useState, useEffect } from 'react'
import { getRecentConversationsClient, getRecentConversationsDeltaClient } from '@/lib/supabase/queries'
import { useUserContext } from '@/components/providers'

/**
 * Recent conversations cache + sync hook
 *
 * Goals
 * - Render the sidebar list instantly from a localStorage cache
 * - On warm start, make exactly one delta request to reconcile cache vs DB
 * - On cold start, fetch the first page once
 * - On infinite scroll, fetch exactly one next page per user action
 * - Keep cache bounded (~100 items) with pruning of the oldest entries
 */

// Local cache config
const CACHE_KEY = 'recent_conversations_cache'
const MAX_CACHE_ITEMS = 100

/**
 * Minimal conversation shape used by the sidebar and cache.
 */
type RecentConversation = {
    id: string
    title: string | null
    updated_at: string
}

/**
 * Type guard that validates an unknown value as a RecentConversation.
 * Accepts title as string | null | undefined to tolerate server variance.
 */
function validateConvoType(obj: unknown): obj is RecentConversation {
    if (!obj || typeof obj !== 'object') return false
    const rec = obj as Record<string, unknown>
    const hasValidDate = typeof rec.updated_at === 'string' && !Number.isNaN(Date.parse(rec.updated_at))
    return typeof rec.id === 'string'
        && hasValidDate
        && (typeof rec.title === 'string' || rec.title === null || typeof rec.title === 'undefined')
}

/**
 * Safely parse a JSON string into RecentConversation[].
 * Returns [] on parse failure and filters out invalid items.
 */
function safeParseArray(json: string | null): RecentConversation[] {
    if (!json) return []
    try {
        const parsedUnknown: unknown = JSON.parse(json)
        if (!Array.isArray(parsedUnknown)) return []
        const filtered: RecentConversation[] = []
        for (const item of parsedUnknown) {
            if (validateConvoType(item)) filtered.push({
                id: item.id,
                title: item.title ?? null,
                updated_at: item.updated_at
            })
        }
        return filtered
    } catch {
        return []
    }
}

/**
 * Read and normalize the localStorage cache. Returns [] on server-side.
 */
function getCache(): RecentConversation[] {
    if (typeof window === 'undefined') return []
    return safeParseArray(localStorage.getItem(CACHE_KEY))
}

/**
 * Return a copy of items sorted by updated_at descending (most recent first).
 */
function sortByUpdatedDesc(items: RecentConversation[]): RecentConversation[] {
    return [...items].sort((a, b) => {
        const at = new Date(a.updated_at).getTime()
        const bt = new Date(b.updated_at).getTime()
        return bt - at
    })
}

/**
 * Remove duplicates by id while preserving the first occurrence ordering.
 */
function dedupeById(items: RecentConversation[]): RecentConversation[] {
    const seen = new Set<string>()
    const out: RecentConversation[] = []
    for (const item of items) {
        if (!item.id) continue
        if (seen.has(item.id)) continue
        seen.add(item.id)
        out.push(item)
    }
    return out
}

/**
 * Save a normalized, pruned cache to localStorage.
 */
function saveCache(items: RecentConversation[]): RecentConversation[] {
    if (typeof window === 'undefined') return items
    let normalized = sortByUpdatedDesc(dedupeById(items))
    if (normalized.length > MAX_CACHE_ITEMS) {
        normalized = normalized.slice(0, MAX_CACHE_ITEMS)
    }
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(normalized))
    } catch {
        // ignore secondary failures
    }
    return normalized
}

/**
 * Fetch a page of recent conversations using offset-based pagination.
 */
async function fetchRecent(limit: number, offset: number): Promise<RecentConversation[]> {
    const raw = await getRecentConversationsClient(limit, offset)
    // normalize unknown payload into typed array
    const normalized: RecentConversation[] = []
    if (Array.isArray(raw)) {
        for (const item of raw as unknown[]) {
            if (validateConvoType(item)) {
                normalized.push({ id: item.id, title: item.title ?? null, updated_at: item.updated_at })
            }
        }
    }
    return normalized
}

/**
 * Fetch conversations updated since a timestamp, excluding provided ids.
 * Used once on warm start to reconcile cache vs DB with a single request.
 */
async function fetchRecentDelta(sinceUpdatedAt: string, excludeIds: string[], limit: number = 100): Promise<RecentConversation[]> {
    const raw = await getRecentConversationsDeltaClient(sinceUpdatedAt, excludeIds, limit)
    const normalized: RecentConversation[] = []
    if (Array.isArray(raw)) {
        for (const item of raw as unknown[]) {
            if (validateConvoType(item)) {
                normalized.push({ id: item.id, title: item.title ?? null, updated_at: item.updated_at })
            }
        }
    }
    return normalized
}

/**
 * Hook powering the sidebar conversation history with cache-first UX and minimal network.
 * - Cold start: fetch first page once.
 * - Warm start: render cache, then do exactly one delta request.
 * - Load more: fetch exactly one next page of older items.
 */
export function useRecentConversations(limit: number = 10) {
    const [conversations, setConversations] = useState<RecentConversation[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [hasMore, setHasMore] = useState(true)
    const { isUserAuthenticated } = useUserContext()

    useEffect(() => {
        let isCancelled = false

        const run = async () => {
            if (!isUserAuthenticated) {
                if (!isCancelled) {
                    setConversations([])
                    setHasMore(true)
                    setLoading(false)
                    setError(null)
                }
                return
            }

            if (!isCancelled) setError(null)

            // 1) Show cache immediately
            const cached = getCache()
            if (!isCancelled) {
                setConversations(sortByUpdatedDesc(cached))
            }

            // 2) Sync with server
            try {
                // Initial load: single delta request
                if (!isCancelled) setLoading(true)

                if (cached.length === 0) {
                    // Cold start: fetch first page
                    const firstPage = await fetchRecent(limit, 0)
                    const newItems = sortByUpdatedDesc(dedupeById(firstPage))
                    if (!isCancelled) {
                        setConversations(newItems)
                        setHasMore((newItems?.length || 0) === limit)
                    }
                    saveCache(newItems)
                    if (!isCancelled) setLoading(false)
                    return
                }

                const cachedSorted = sortByUpdatedDesc(cached)
                const idsInCache = new Set<string>(cachedSorted.map((c) => c.id))
                const oldestCached = cachedSorted[cachedSorted.length - 1]

                // One delta request: items updated since oldest cached, excluding cached IDs
                const delta = await fetchRecentDelta(
                    oldestCached.updated_at,
                    Array.from(idsInCache)
                )

                const merged = sortByUpdatedDesc(dedupeById([...cached, ...delta]))
                if (!isCancelled) {
                    setConversations(merged)
                    // Assume more exists until proven otherwise on explicit loadMore
                    setHasMore(true)
                }
                saveCache(merged)
                if (!isCancelled) setLoading(false)
            } catch (err) {
                if (!isCancelled) setError('Failed to fetch recent conversations')
                console.error('Error syncing recent conversations:', err)
                if (!isCancelled) setLoading(false)
            }
        }

        run()
        return () => {
            isCancelled = true
        }
    }, [limit, isUserAuthenticated])

    /**
     * Fetch the next page of older conversations (imperative).
     * Uses current visible count as the server offset.
     */
    const loadMore = async () => {
        if (loading || !hasMore) return
        setLoading(true)
        try {
            const serverOffset = conversations.length
            const incoming = await fetchRecent(limit, serverOffset)
            const mergedVisible = sortByUpdatedDesc(dedupeById([...conversations, ...incoming]))
            setConversations(mergedVisible)
            setHasMore((incoming.length || 0) === limit)
            const currentCache = getCache()
            saveCache([...currentCache, ...incoming])
        } catch (err) {
            setError('Failed to load more conversations')
            console.error('Error loading more conversations:', err)
        } finally {
            setLoading(false)
        }
    }

    /**
     * Reset pagination and visible list (cache remains persisted).
     */
    const reset = () => {
        setConversations([])
        setHasMore(true)
        setError(null)
    }

    return { conversations, loading, error, loadMore, hasMore, reset }
}